// The "you can close this" notice for a Flow reply that runs long: when it shows
// and which wording. Rendered by components/chat/LongTurnBanner.

import { useEffect, useState } from 'react'
import type { ChatMessage } from '@/hooks/useChat'

/** Shown this long after the reply was sent. Half of Flow replies finish within
 *  ~20 s (Sentry, 14 days to 2026-10-07), so most coaches never see it. */
export const LONG_TURN_AFTER_MS = 20_000

/** Tools that run on the coach's computer through Beoflow Desktop and can take a
 *  while. Mirrors the backend's DESKTOP_AUTHORING_TOOL_NAMES
 *  (beoflow ai_agent/shared_context/tool_names.py) minus list_local_tools, which
 *  answers at once and should not raise the notice. */
const DESKTOP_TOOLS = new Set([
  'pick_file', 'find_files', 'inspect_file', 'probe_source', 'observe_portal',
  'synthesize_ingest_tool', 'test_ingest_tool', 'submit_for_approval',
])
const isDesktopTool = (tool: string) => DESKTOP_TOOLS.has(tool) || tool.startsWith('local_')

/** The computer the desktop call ran on, when a tool result already names it. */
function computerFrom(message: ChatMessage): string | null {
  for (const tc of message.toolCalls ?? []) {
    if (!isDesktopTool(tc.tool) || tc.result == null) continue
    const found = JSON.stringify(tc.result).match(/"on_computer"\s*:\s*"([^"]{1,60})"/)
    if (found) return found[1]
  }
  return null
}

export function useLongTurnBanner(messages: ChatMessage[], isStreaming: boolean) {
  const last = messages[messages.length - 1]
  const turn = isStreaming && last?.role === 'assistant' && last.isStreaming ? last : null
  const turnId = turn?.id ?? null
  const [elapsedFor, setElapsedFor] = useState<string | null>(null)
  const [dismissedFor, setDismissedFor] = useState<string | null>(null)

  useEffect(() => {
    if (!turnId) return
    const timer = setTimeout(() => setElapsedFor(turnId), LONG_TURN_AFTER_MS)
    return () => clearTimeout(timer)
  }, [turnId])

  const desktop = !!turn?.toolCalls?.some(tc => isDesktopTool(tc.tool))
  const visible = !!turnId && dismissedFor !== turnId && (desktop || elapsedFor === turnId)
  return {
    visible,
    desktop,
    computer: turn ? computerFrom(turn) : null,
    dismiss: () => setDismissedFor(turnId),
  }
}
